import "./deadletterqueue.scss";
import React from 'react';
import classNames from "classnames";
import { useFetch } from "./fetch";
import { ErrorBoundary } from "react-error-boundary";
import { useQuery, useMutation } from 'react-query';

export const DeadLetterQueues = ({ prefix }) => {
    const [selected, setSelect] = React.useState(null);
    if (!selected) {
        return <DeadLetterQueueSelector onSelect={ setSelect } prefix={ prefix } />;
    }
    return <DeadLetterQueueDetailView
        queue={ selected }
        onUnselect={ () => setSelect(null) }
    />;
};

const DeadLetterQueueSelector = ({ onSelect, prefix }) => {
    const fetch = useFetch();
    const queues = useQuery(['dlq'], async () => {
        const httpGET = await fetch(`/dead-letter/${prefix}`);
        if (!httpGET.ok) {
            throw new Error(await httpGET.text());
        }
        if (httpGET.status === 204) {
            return [];
        }
        return await httpGET.json();
    }, {
        retry: 3,
        staleTime: 1000 * 600,
    });

    if (queues.isSuccess) {
        return <section className="section">
            <div className="container">
                <ErrorBoundary fallback={ <div>:cpt:</div>}>
                    { queues.data.length
                        ? <DeadLetterQueueListView queues={ queues.data } onSelect={ onSelect } />
                        : <NoQueues refetch={ queues.refetch } /> }
                </ErrorBoundary>
            </div>
        </section>;
    }
    if (queues.isError) {
        return  <pre>{String(queues.error)}</pre>;
    }
    return <progress className="progress" />;
}

const NoQueues = ({ refetch }) => {
    return <div className="notification">
        <p>
            No messages in DLQ
        </p>
        <button className="button is-outlined" onClick={ refetch }>
            <span>
                Refresh
            </span>
            <span className="icon">
                <i className="mdi mdi-refresh"></i>
            </span>
        </button>
    </div>
}

const DeadLetterQueueListView = ({ queues, onSelect }) =>  queues.map(q => <DeadLetterQueue
    key={ q.url }
    queue={ q }
    onClick={ () => onSelect(q) }
/>);

const DeadLetterQueueDetailView = ({ queue, onUnselect }) => {
    return <div>
        <nav className="level">
            <div className="level-left">
                <div className="level-item">
                    <h1 className="title">{ queue.name }</h1>
                </div>
            </div>
            <div className="level-right">
                <div className="level-item">
                    <p className="subtitle"><QueueTags queue={ queue } /></p>
                </div>
                <p className="level-item">
                    <button className="button" onClick={ onUnselect }>
                        Back
                    </button>
                </p>
            </div>
        </nav>
        <ErrorBoundary fallback={ <div>:cpt:</div>}>
            <DeadLetterQueueMessage queue={ queue } />
        </ErrorBoundary>
    </div>;
};

function pickStateFn(state, action) {
    switch(action) {
        case 'pick':
            return {
                picked: true
            }
        case 'hover-ignore':
            return {
                picked: true,
                hover: 'ignore',
            }
        case 'exit-ignore':
            return {
                picked: true,
            }
        case 'hover-retry':
            return {
                picked: true,
                hover: 'retry',
            }
        case 'exit-retry':
            return {
                picked: true,
            }
        case 'release':
            return {
                picked: false
            }
        default:
            return state;
    }
}

const DropColumn = ({ as, onDragEnter, onDragLeave, onDrop, ...props }) => {
    const As = as || 'div';
    const counter = React.useRef(0);

    const enter = React.useCallback((ev) => {
        if (!counter.current) {
            onDragEnter(ev);
        }
        counter.current += 1;
    }, [onDragEnter]);
    const leave = React.useCallback((ev) => {
        counter.current -= 1;
        if (!counter.current) {
            onDragLeave(ev);
        }
    }, [onDragLeave]);
    const drop = React.useCallback((ev) => {
        counter.current = 0;
        ev.preventDefault();
        onDragLeave(ev);
        onDrop(ev);
    }, [onDragLeave, onDrop]);

    return <As
        onDragEnter={ enter }
        onDragLeave={ leave }
        onDrop={ drop }
        onDragOver={e => {
            e.preventDefault()
        }}
        {...props}>
    </As>;
}

function fmt(value, type) {
    switch(type) {
        case 'ApproximateFirstReceiveTimestamp':
        case 'SentTimestamp':
            return <abbr title={ new Date(parseInt(value)).toLocaleString() }>
                {value}
            </abbr>
        default:
            return value;
    }
}

const DeadLetterQueueMessage = ({ queue }) => {
    const [state, dispatch] = React.useReducer(pickStateFn, {});

    const fetch = useFetch();
    const message = useQuery(['dlq', queue.url], async () => {
        const httpGET = await fetch(queue.url);
        if (!httpGET.ok) {
            throw new Error(await httpGET.text());
        }
        if (httpGET.status === 204) {
            return null;
        }
        return await httpGET.json();
    }, {
        staleTime: Infinity,
        refetchOnMount: 'always',
    });

    const { mutate, isError, isLoading, error } = useMutation(async (method) => {
        const resp = await fetch(message.data.url, {
            method,
        });
        if (!resp.ok) {
            throw new Error(await resp.text());
        }
    }, {
        enabled: message.isSuccess && message.data,
        onSuccess: () => {
            message.refetch();
        }
    });

    const retry = React.useCallback((ev) => {
        mutate('POST');
    }, [mutate]);
    const ignore = React.useCallback((ev) => {
        mutate('DELETE');
    }, [mutate]);


    if (message.isLoading) {
        return <progress className="progress" />;
    }

    if (message.data === null) {
        return <div className="notification is-info">
            Queue is empty
        </div>;
    }


    return <div className="block dlq">
        {isError && <div className="notification is-danger"><pre>{String(error)}</pre></div>}
        {isLoading && <progress className="progress" />}
        <div className="columns is-vcentered" >
            <DropColumn
                className={ classNames("column is-one-fifth has-text-centered", {
                    "has-background-success": state.hover === 'retry',
                    "has-text-white": state.hover === 'retry',
                }) }
                onDragEnter={ () => dispatch('hover-retry') }
                onDragLeave={ () => dispatch('exit-retry') }
                onDrop={ retry }
            >
                <p className={ classNames({
                    "is-hidden": !state.picked,
                }) }>
                    <span className="icon is-size-1">
                        <i className="mdi mdi-reiterate"></i>
                    </span>
                    <p>Redrive</p>
                </p>
            </DropColumn>
            <div className="column is-three-fifth">
                <pre
                    draggable
                    onDragStart={ () => dispatch('pick') }
                    onDragEnd={ () => dispatch('release') }
                    className="dlq-message"
                >{message.data.body}</pre>
                <ul>
                    {
                        Object.keys(message.data.attributes).map(k => 
                            <li key={ k }>
                                <strong>{k}</strong>:
                                {fmt(message.data.attributes[k], k)}
                            </li>
                         )
                    }
                </ul>
            </div>
            <DropColumn
                className={ classNames("column is-one-fifth has-text-centered", {
                    "has-background-danger": state.hover === 'ignore',
                    "has-text-white": state.hover === 'ignore',
                }) }
                onDragEnter={ () => dispatch('hover-ignore') }
                onDragLeave={ () => dispatch('exit-ignore') }
                onDrop={ ignore }
            >
                <p className={ classNames({
                    "is-hidden": !state.picked,
                }) }>
                    <span className="icon is-size-1">
                        <i className="mdi mdi-delete"></i>
                    </span>
                    <p>Ignore</p>
                </p>
            </DropColumn>
        </div>
    </div>;
}

const DeadLetterQueue = ({ queue, onClick }) => {
    return <div className="card">
        <div className="card-header">
            <p className="card-header-title">
                <button className="button is-white" onClick={ onClick }>
                    {queue.name}
                </button>
            </p>
            <p className="card-header-icon">
                <QueueTags queue={ queue } />
            </p>
        </div>
        <div className="card-content">
            <p className="has-text-centered has-text-weight-bold">
                <button className="button is-large is-size-1 is-white" onClick={ onClick }>
                    {queue.size}
                </button>
            </p>
        </div>
    </div>;
};

const QueueTags = ({ queue }) => {
    return <>
        { queue.tags.Kind ? <span className="tag is-danger">{ queue.tags.Kind }</span> : '' }
        { queue.tags.Project ? <span className="tag is-warning">{ queue.tags.Project }</span> : '' }
        { queue.tags.Environment ? <span className="tag is-info">{ queue.tags.Environment }</span> : '' }
    </>;
}
